/**
 * Redação de material sensível.
 *
 * Fonte normativa: Plano Oficial, seções 13 ("Nenhum segredo de certificado em
 * logs, erros públicos ou webhooks") e 18 ("Segredos cifrados e fora de
 * respostas, logs e erros públicos"); Script Mestre, seção 12.
 *
 * Regra: nenhum caminho de log, resposta de API ou payload de webhook pode
 * carregar senha de certificado, conteúdo de PFX, chave de API em claro,
 * service role, credencial municipal ou token.
 */

const SENSITIVE_KEY_FRAGMENTS = [
  'password',
  'senha',
  'passphrase',
  'secret',
  'token',
  'apikey',
  'api_key',
  'authorization',
  'servicerole',
  'service_role',
  'privatekey',
  'private_key',
  'pfx',
  'p12',
  'certificatecontent',
  'certificate_content',
  'credential',
  'credencial',
  'clientsecret',
  'client_secret',
  'anonkey',
  'anon_key',
  'hmac',
  'signingkey',
  'signing_key',
];

export const REDACTED = '[REDACTED]';

/**
 * Normaliza a chave preservando dígitos: `p12` precisa continuar sendo `p12`.
 * Reduzir o fragmento a letras transformaria `p12` em `p` e redigiria
 * praticamente qualquer campo.
 */
function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const NORMALIZED_SENSITIVE_FRAGMENTS = SENSITIVE_KEY_FRAGMENTS.map(normalizeKey);

function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key);
  return NORMALIZED_SENSITIVE_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

/**
 * Redige recursivamente um objeto antes de log/resposta/webhook.
 *
 * Falha fechada: chave desconhecida com nome sensível é redigida, não
 * inspecionada. Cadeias muito longas em campos binários também são cortadas.
 */
export function redact<T>(value: T, depth = 0): unknown {
  if (depth > 12) return REDACTED;
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((item) => redact(item, depth + 1));
  if (typeof value !== 'object') return value;

  const source = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(source)) {
    out[key] = isSensitiveKey(key) ? REDACTED : redact(entry, depth + 1);
  }
  return out;
}

/**
 * Mascara uma chave de API para exibição/auditoria, preservando apenas o
 * prefixo. O plaintext completo é exibido uma única vez, no momento da
 * criação (Plano Oficial, 14.2).
 */
export function maskApiKey(plaintext: string): string {
  if (plaintext.length <= 8) return REDACTED;
  return `${plaintext.slice(0, 8)}${'*'.repeat(Math.min(plaintext.length - 8, 24))}`;
}
